import React, { useEffect, useState } from "react";
import "./SpecialEventEnrol.scss";
import {
  FxSelect,
  FxInput,
  http,
  localstorage,
  formatService,
  formValidator,
} from "../../fx-core";
import { Button } from "@material-ui/core";

interface Props {
  id: number;
  handleClose: () => void;
}

interface Item {
  serviceId: number;
  communityId: number;
  stallId: number;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

interface Option {
  id: number;
  text?: string;
  code?: string;
  enabled?: boolean;
  [key: string]: any;
}

interface Question {
  id: number;
  description: string;
  questionTypeId: number;
  identifier?: string;
}

interface QuestionOption {
  id: number;
  questionId: number;
  optionText: string;
  code?: string;
}

interface CommunityValidation {
  entryFee: number;
  startDateTime: string;
  endDateTime: string;
  name?: string;
}

// ─── Plan types ───────────────────────────────────────────────────────────────
const PLAN_OPTIONS: Option[] = [
  { id: 1, text: "Day Plans", code: "day", enabled: true },
  // Simply remove the other two entries until they're ready
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toYMD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatDMY = (dateStr: string): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const DAY_LABELS   = ["SUN","MON","TUE","WED","THU","FRI","SAT"];
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const generateDateRange = (start: string, end: string): string[] => {
  const dates: string[] = [];
  const cur  = new Date(start); cur.setHours(0,0,0,0);
  const last = new Date(end);   last.setHours(0,0,0,0);
  while (cur <= last) { dates.push(toYMD(new Date(cur))); cur.setDate(cur.getDate()+1); }
  return dates;
};

const isConsecutive = (sorted: string[]): boolean => {
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i-1]);
    prev.setDate(prev.getDate() + 1);
    if (toYMD(prev) !== sorted[i]) return false;
  }
  return true;
};

// ─── Component ────────────────────────────────────────────────────────────────
const SpecialEventEnrol: React.FC<Props> = (props) => {
  const isViewMode = props.id > 0;

  const defaultItem: Item = {
    serviceId: -1, communityId: -1, stallId: -1,
    startDate: "", startTime: "00:00", endDate: "", endTime: "23:59",
  };

  const [item,                  setItem]                  = useState<Item>(defaultItem);
  const [errors,                setErrors]                = useState<Record<string,any>>({});
  const [stalls,                setStalls]                = useState<Option[]>([]);
  const [services,              setServices]              = useState<Option[]>([]);
  const [splCommunities,        setSplCommunities]        = useState<Option[]>([]);
  const [questions,             setQuestions]             = useState<Question[]>([]);
  const [queOptions,            setQueOptions]            = useState<QuestionOption[]>([]);
  const [answerMap,             setAnswerMap]             = useState<Record<string,string>>({});
  const [communityValidation,   setCommunityValidation]   = useState<CommunityValidation | null>(null);
  const [serviceSelectDisabled, setServiceSelectDisabled] = useState(false);
  const [serviceInactiveMsg,    setServiceInactiveMsg]    = useState("");
  const [selectedPlanId,        setSelectedPlanId]        = useState<number>(-1);
  const [availableDates,        setAvailableDates]        = useState<string[]>([]);
  const [bookedDates,           setBookedDates]           = useState<string[]>([]);
  const [selectedDates,         setSelectedDates]         = useState<string[]>([]);
  const [dateError,             setDateError]             = useState("");
  const [viewData,              setViewData]              = useState<any>(null);
  const [viewPayment,           setViewPayment]           = useState<any>(null);
  const [selectedMonth,         setSelectedMonth]         = useState<string>("");

  const entryFee        = communityValidation?.entryFee ?? 0;
  const totalCost       = selectedDates.length * entryFee;
  const selectedPlanCode = PLAN_OPTIONS.find(p => p.id === selectedPlanId)?.code ?? "";

  const handleItemChange = (data: Partial<Item>) => setItem(prev => ({ ...prev, ...data }));
  const updateErrors     = (data: Record<string,any>) => setErrors(prev => ({ ...prev, ...data }));
  const hasError         = (f: string, m: string) => errors?.[f]?.[m];
  const handleClose      = () => props.handleClose();

  const isFormReady =
    item.stallId > -1 &&
    item.communityId > -1 &&
    selectedPlanId > -1 &&
    selectedDates.length > 0 &&
    item.serviceId > -1 &&
    !serviceInactiveMsg;

  // ── Month filter derived state ────────────────────────────────────────────
  const visibleDates    = availableDates.filter(d => !bookedDates.includes(d));
  const showMonthFilter = visibleDates.length > 30;

  // Unique "YYYY-MM" keys in order
  const monthGroups = Array.from(new Set(visibleDates.map(d => d.slice(0, 7))));

  // Dates shown in the chip grid — filtered by month when filter is active
  const displayedDates = showMonthFilter && selectedMonth
    ? visibleDates.filter(d => d.startsWith(selectedMonth))
    : visibleDates;

  // Auto-select the first available month whenever visibleDates loads (>30)
  useEffect(() => {
    if (showMonthFilter && monthGroups.length > 0 && !selectedMonth) {
      setSelectedMonth(monthGroups[0]);
    }
  }, [visibleDates]);

  // ── Lookups ──────────────────────────────────────────────────────────────────
  const initLookup = () => {
    http.post({
      url: "options/lookup",
      data: {
        lookups: { ProviderStalls: { default: true }, SpecialCommunities: { default: true } },
        filters: { userId: localstorage.getItem("userId"), communityId: localstorage.getItem("communityId") },
      },
    }).then((res: any) => {
      setStalls(res.data.ProviderStalls);
      setSplCommunities(res.data.SpecialCommunities);
      if (props.id > 0) getStallCommunityMapByServiceReqId();
    });
  };

  const getCommunityServices = async (communityId: number): Promise<any[]> => {
    const res = await http.get({ url: `portal/community/services/${communityId}` });
    console.log("communityServices raw:", res.data);
    return res.data?.services || res.data || [];
  };

  const checkSingleStallService = async (
    stallId: number,
    communityServices: any[]
  ): Promise<{ serviceId: number; serviceName: string; blocked: boolean }> => {
    const res    = await http.get({ url: `portal/stall/${stallId}/servicelist` });
    const result = res.data.services || [];
    const transformed = result.map((s: any) => ({
      id:        s.id ?? -1,
      text:      s.serviceName ?? "Unknown",
      isDefault: !!(s.defaultBehaviorId || s.isDefault || s.defaultBehaviorCode),
    }));
    if (transformed.length === 0) return { serviceId: -1, serviceName: "", blocked: false };
    const primary = transformed.find((s: any) => s.isDefault) || transformed[0];
    if (communityServices.length > 0) {
      const match = communityServices.find(cs => cs.id === primary.id);
      if (!match || !match.isCommunityMapped) {
        return { serviceId: primary.id, serviceName: primary.text, blocked: true };
      }
    }
    return { serviceId: primary.id, serviceName: primary.text, blocked: false };
  };

  const getStallService = async (
    stallId: number,
    communityServices: any[],
    communityName: string,
    allStalls: Option[]
  ) => {
    console.log("allStalls received:", allStalls);
    console.log("communityServices:", communityServices);
    setServiceInactiveMsg("");

    if (stallId === -1) {
      const realStalls = allStalls.filter(s => s.id > 0);
      if (realStalls.length === 0) return;

      const blockedEntries: string[] = [];
      let firstValidStallId = -1;

      for (const stall of realStalls) {
        const { serviceName, blocked } = await checkSingleStallService(stall.id, communityServices);
        if (blocked) {
          blockedEntries.push(`${stall.text} (${serviceName})`);
        } else if (firstValidStallId === -1) {
          firstValidStallId = stall.id;
        }
      }

      const firstStallId = realStalls[0].id;
      const res = await http.get({ url: `portal/stall/${firstStallId}/servicelist` });
      const transformed: Option[] = (res.data.services || []).map((s: any) => ({
        id: s.id ?? -1, text: s.serviceName ?? "Unknown",
        code: s.serviceCode ?? "", isDefault: !!(s.defaultBehaviorId || s.isDefault || s.defaultBehaviorCode),
        attributes: null,
      }));
      setServices(transformed);

      if (blockedEntries.length > 0) {
        setServiceInactiveMsg(
          `The following stall(s) have an inactive service in ${communityName}: ${blockedEntries.join(", ")}. Please contact admin@localites.in.`
        );
        setServiceSelectDisabled(true);
        handleItemChange({ serviceId: -1 });
        return;
      }

      const def = transformed.find(s => s.isDefault);
      if (def) { handleItemChange({ serviceId: def.id }); setServiceSelectDisabled(true); }
      else      { handleItemChange({ serviceId: -1 });     setServiceSelectDisabled(false); }
      return;
    }

    const res    = await http.get({ url: `portal/stall/${stallId}/servicelist` });
    const result = res.data.services || [];
    const transformed: Option[] = result.map((s: any) => ({
      id:        s.id ?? -1,
      text:      s.serviceName ?? "Unknown",
      code:      s.serviceCode ?? "",
      isDefault: !!(s.defaultBehaviorId || s.isDefault || s.defaultBehaviorCode),
      attributes: null,
    }));
    setServices(transformed);

    if (transformed.length > 0) {
      const primary   = transformed.find(s => s.isDefault) || transformed[0];
      const stallName = allStalls.find(s => s.id === stallId)?.text || "Your stall";
      if (communityServices.length > 0) {
        const match = communityServices.find(cs => cs.id === primary.id);
        if (!match || !match.isCommunityMapped) {
          setServiceInactiveMsg(
            `${stallName} (${primary.text}) is inactive in ${communityName}. Please contact admin@localites.in.`
          );
          handleItemChange({ serviceId: -1 });
          setServiceSelectDisabled(true);
          return;
        }
      }
    }

    const def = transformed.find(s => s.isDefault);
    if (def) {
      handleItemChange({ serviceId: def.id });
      setServiceSelectDisabled(true);
    } else if (transformed.length === 1) {
      handleItemChange({ serviceId: transformed[0].id });
      setServiceSelectDisabled(false);
    } else {
      handleItemChange({ serviceId: -1 });
      setServiceSelectDisabled(false);
    }
  };

  const validateCommunityCode = (code: string) => {
    http.post({ url: `portal/validate/community/code`, data: { code } }).then((res: any) => {
      const data = res.data;
      setCommunityValidation(data.community);
      if (data.community?.startDateTime && data.community?.endDateTime) {
        setAvailableDates(generateDateRange(
          toYMD(new Date(data.community.startDateTime)),
          toYMD(new Date(data.community.endDateTime))
        ));
      }
    });
  };

  const fetchBookedDates = async (stallId: number, communityId: number) => {
    if (stallId <= 0 || communityId <= 0) return;
    const res = await http.get({ url: `community/stall/${stallId}/community/${communityId}/bookeddates` });
    setBookedDates(res.data || []);
  };

  const toggleDate = (date: string) => {
    setDateError("");
    setSelectedDates(prev => {
      const next = prev.includes(date)
        ? prev.filter(d => d !== date)
        : [...prev, date].sort();
      if (next.length > 1 && !isConsecutive(next)) {
        setDateError("Please select consecutive dates only. For non-consecutive dates, enroll separately for each date.");
        return prev;
      }
      handleItemChange(
        next.length > 0
          ? { startDate: next[0], endDate: next[next.length-1] }
          : { startDate: "", endDate: "" }
      );
      return next;
    });
  };

  const handlePlanChange = (input: { name: string; value: any }) => {
    const plan = PLAN_OPTIONS.find(p => p.id === input.value);
    if (!plan?.enabled) return;
    setSelectedPlanId(input.value);
    setSelectedDates([]);
    setDateError("");
    handleItemChange({ startDate: "", endDate: "" });
  };

  const handleInputChange = async (input: { name: string; value: any }) => {
    handleItemChange({ [input.name]: input.value });
    const vm: Record<string,string[]> = { stallId:["select"], communityId:["select"], serviceId:["select"] };
    if (vm[input.name]) updateErrors({ [input.name]: formValidator.validate({ ...input, validations: vm[input.name] }) });

    if (input.name === "communityId") {
      setSelectedDates([]); setAvailableDates([]); setBookedDates([]);
      setDateError(""); setServiceInactiveMsg("");
      setSelectedMonth(""); // reset month filter
      handleItemChange({ serviceId: -1, startDate: "", endDate: "" });
      const sel = splCommunities.find(c => c.id === input.value);
      if (sel?.code) validateCommunityCode(sel.code);
      getCommunityQuestionOptions(input.value);
      if (input.value > -1) {
        const cs = await getCommunityServices(input.value);
        await getStallService(item.stallId, cs, sel?.text || "", stalls);
        if (item.stallId > 0) await fetchBookedDates(item.stallId, input.value);
      }
    }

    if (input.name === "stallId") {
      setSelectedDates([]); setDateError(""); setServiceInactiveMsg("");
      setSelectedMonth(""); // reset month filter
      handleItemChange({ serviceId: -1, startDate: "", endDate: "" });
      if (item.communityId > -1) {
        const sel = splCommunities.find(c => c.id === item.communityId);
        const cs  = await getCommunityServices(item.communityId);
        await getStallService(input.value, cs, sel?.text || "", stalls);
        if (input.value > -1) await fetchBookedDates(input.value, item.communityId);
      }
    }
  };

  const bulkValidate = (): boolean => {
    const vItems = [
      { name: "stallId",     value: item.stallId,     validations: ["select"] },
      { name: "communityId", value: item.communityId, validations: ["select"] },
      { name: "serviceId",   value: item.serviceId,   validations: ["select"] },
    ];
    const result = formValidator.bulkValidate(vItems);
    updateErrors(result.errors);
    if (selectedPlanId === -1) { setDateError("Please select a plan."); return false; }
    if (selectedDates.length === 0) { setDateError("Please select at least one date."); return false; }
    if (serviceInactiveMsg) return false;
    return result.isValid;
  };

  const submitServiceRequest = () => {
    if (!bulkValidate()) return;
    const queAnswers   = prepareAnswers();
    const startDateStr = selectedDates[0];
    const endDateStr   = selectedDates[selectedDates.length - 1];

    http.post({
      url: `servicerequest/special/comunity/save`,
      data: {
        serviceRequest: {
          serviceId:   item.serviceId,
          communityId: item.communityId,
          stallId:     item.stallId,
          currentDate: formatService.getCurrentDateForServer(),
          startDate:   `${startDateStr} 00:00:00`,
          startTime:   "12:00 AM",
          endDate:     `${endDateStr} 23:59:00`,
          endTime:     "11:59 PM",
        },
        serviceRequestQuestionAnswers: queAnswers,
      },
    }).then(() => handleClose());
  };

  const getCommunityQuestionOptions = (communityId: number) => {
    if (communityId > -1) {
      http.get({ url: `question/community/${communityId}/1` }).then((res: any) => {
        const result = res.data;
        const ansMap: Record<string,string> = {};
        result.questions.forEach((q: any) => { ansMap[q.id.toString()] = ""; q.identifier = q.id.toString(); });
        result.questionOptions.forEach((qo: any) => { qo.code = qo.id.toString(); });
        setAnswerMap(ansMap); setQuestions(result.questions); setQueOptions(result.questionOptions);
      });
    } else { setQuestions([]); setQueOptions([]); }
  };

  const prepareAnswers = () => {
    const answers: any[] = [];
    for (const key in answerMap) {
      const que = questions.find(q => q.id === parseInt(key));
      if (!que) continue;
      if (que.questionTypeId === 3) {
        const opt = queOptions.find(o => o.questionId === parseInt(key) && o.id.toString() === answerMap[key]);
        if (opt) answers.push({ questionId: parseInt(key), answerOptionId: answerMap[key], answerText: opt.optionText });
      } else {
        answers.push({ questionId: parseInt(key), answerText: answerMap[key] });
      }
    }
    return answers;
  };

  const handleAnswerInputChange = (input: { name: string; value: string }) =>
    setAnswerMap(prev => ({ ...prev, [input.name]: input.value }));

  const getStallCommunityMapByServiceReqId = () => {
    http.get({ url: `stallcommunitymap/servicerequest/${props.id}` }).then((res: any) => {
      setViewData(res.data); setItem(res.data);
      if (res.data.communityId > -1) getCommunityQuestionOptions(res.data.communityId);
      getServiceReqQueAns(res.data.serviceRequestId);
     if (res.data.serviceRequestId) {
        http.get({ url: `payment/servicerequest/${res.data.serviceRequestId}` }).then((payRes: any) => {
          console.log("viewPayment data:", payRes.data);
          setViewPayment(payRes.data);
        }).catch((err) => {
          console.log("viewPayment error:", err);
        });
      }
    });
  };

  const getServiceReqQueAns = (id: number) => {
    http.post({ url: `servicerequest/question/answers`, data: { id } }).then((res: any) => {
      const ansMap: Record<string,string> = {};
      res.data.forEach((r: any) => {
        ansMap[r.questionId.toString()] = r.answerOptionId > 0 ? r.answerOptionId.toString() : r.answerText;
      });
      setAnswerMap(ansMap);
    });
  };

  useEffect(() => { initLookup(); }, []);

  const todayYMD = toYMD(new Date());

  // ─────────────────────────────────────────────────────────────────────────────
  // VIEW MODE
  // ─────────────────────────────────────────────────────────────────────────────
  if (isViewMode) {
    const v        = viewData || item;
    const startStr = v.startDate ? formatDMY((v.startDate as string).split(" ")[0]) : "-";
    const endStr   = v.endDate   ? formatDMY((v.endDate   as string).split(" ")[0]) : "-";
    const startTime = v.startTime || "-";
    const endTime   = v.endTime   || "-";

    return (
      <div className="spleventenrol-ui">
        <div className="row title-font py-1 px-2 drawer-header">
          <span className="col-11">Special Event — View</span>
          <span className="col-1 text-center">
            <i className="fas fa-times-circle font-16 pointer" onClick={handleClose}></i>
          </span>
        </div>
        <div className="p-4 mb-5">
          <div className="view-card mb-3">
            <ViewRow label="Stall"          value={v.stallName            || "-"} />
            <ViewRow label="Community"      value={v.communityName        || "-"} />
            <ViewRow label="Service"        value={v.serviceName          || "-"} />
            <ViewRow label="Plan Type"      value="Day Plans"                     />
            <div className="view-divider" />
            <ViewRow label="Start Date"     value={startStr}                      />
            <ViewRow label="Start Time"     value={startTime}                     />
            <ViewRow label="End Date"       value={endStr}                        />
            <ViewRow label="End Time"       value={endTime}                       />
            <div className="view-divider" />
            <ViewRow
                label="Amount"
                value={
                    viewPayment?.discountAmount > 0
                        ? `₹${viewPayment.originalAmount - viewPayment.discountAmount} (was ₹${viewPayment.originalAmount}, saved ₹${viewPayment.discountAmount})`
                        : v.amount != null
                            ? `₹${v.amount}`
                            : "-"
                }
            />
            <ViewRow label="Payment Status" value={v.paymentStatus        || "Pending"} />
            <ViewRow label="Event Status"   value={v.serviceRequestStatus || "-"} />
          </div>
          {questions.length > 0 && (
            <div className="view-card">
              {questions.map(que => (
                <ViewRow key={que.id} label={que.description}
                  value={
                    que.questionTypeId === 3
                      ? queOptions.find(o => o.id.toString() === answerMap[que.identifier ?? ""])?.optionText || "-"
                      : answerMap[que.identifier ?? ""] || "-"
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // ENROLL MODE
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="spleventenrol-ui">
      <div className="row title-font py-1 px-2 drawer-header">
        <span className="col-11">Special Event Enroll</span>
        <span className="col-1 text-center">
          <i className="fas fa-times-circle font-16 pointer" onClick={handleClose}></i>
        </span>
      </div>

      <div className="mb-4 pb-5">

        {/* Stall */}
        <div className="col-12 p-3 mt-5">
          <FxSelect name="stallId" variant="outlined" label="Stall"
            options={stalls} selectedValue={item.stallId}
            valueField="id" displayField="text" size="small"
            onValueChange={handleInputChange} />
          {hasError("stallId", "select") && <div className="error">Stall is required</div>}
        </div>

        {/* Community */}
        <div className="col-12 p-3">
          <FxSelect name="communityId" variant="outlined" label="Community"
            options={splCommunities} selectedValue={item.communityId}
            valueField="id" displayField="text" size="small"
            onValueChange={handleInputChange} />
          {hasError("communityId", "select") && <div className="error">Community is required</div>}
          {communityValidation && (
            <div className="community-info-box mt-2">
              <div><b>Entry Fee per day:</b> ₹{communityValidation.entryFee}</div>
              <div><b>Event Start:</b> {formatDMY(communityValidation.startDateTime)}</div>
              <div><b>Event End:</b>   {formatDMY(communityValidation.endDateTime)}</div>
            </div>
          )}
        </div>

        {/* Plan Type */}
        <div className="col-12 p-3">
          <FxSelect
            name="planId"
            variant="outlined"
            label="Select Plan"
            options={PLAN_OPTIONS}
            selectedValue={selectedPlanId}
            valueField="id"
            displayField="text"
            disabledField="disabled"
            size="small"
            onValueChange={handlePlanChange}
          />
          {selectedPlanId === -1 && dateError === "Please select a plan." && (
            <div className="error">{dateError}</div>
          )}
        </div>

        {/* Date chips */}
        {selectedPlanCode === "day" && item.communityId > -1 && (
          <div className="col-12 p-3">
            <div className="section-label mb-2">Select Date(s)</div>

            {visibleDates.length === 0 ? (
              <div className="info-msg">No available dates for this community.</div>
            ) : (
              <>
                {/* ── Month filter tabs (only when >30 dates) ── */}
                {showMonthFilter && (
                  <div className="month-filter-row mb-3">
                    {monthGroups.map(ym => {
                      const [y, m] = ym.split("-");
                      const label  = `${MONTH_LABELS[parseInt(m) - 1]} ${y}`;
                      // Count how many selectable (non-past) dates exist in this month
                      const selectableCount = visibleDates.filter(
                        d => d.startsWith(ym) && d >= todayYMD
                      ).length;
                      // Count already-selected dates in this month
                      const selectedCount = selectedDates.filter(d => d.startsWith(ym)).length;
                      return (
                        <button
                          key={ym}
                          className={[
                            "month-tab",
                            selectedMonth === ym ? "month-tab--active" : "",
                            selectableCount === 0 ? "month-tab--past" : "",
                          ].join(" ").trim()}
                          onClick={() => setSelectedMonth(ym)}>
                          {label}
                          {selectedCount > 0 && (
                            <span className="month-tab__badge">{selectedCount}</span>
                          )}
                          {selectableCount === 0 && (
                            <span className="month-tab__past-hint"> (past)</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* ── Date chip grid ── */}
                <div className="date-chips-row">
                  {displayedDates.map(d => {
                    const dt         = new Date(d + "T00:00:00");
                    const isPast     = d < todayYMD;
                    const isSelected = selectedDates.includes(d);
                    return (
                      <button key={d} disabled={isPast}
                        onClick={() => !isPast && toggleDate(d)}
                        className={["date-chip", isPast ? "date-chip--past" : "", isSelected ? "date-chip--selected" : ""].join(" ").trim()}>
                        <span className="date-chip__day">{DAY_LABELS[dt.getDay()]}</span>
                        <span className="date-chip__num">{dt.getDate()}</span>
                        <span className="date-chip__month">{MONTH_LABELS[dt.getMonth()]}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {dateError && dateError !== "Please select a plan." && (
              <div className="error mt-2">
                <i className="fas fa-exclamation-triangle mr-1"></i>{dateError}
              </div>
            )}
            {selectedDates.length > 0 && (
              <div className="cost-breakdown mt-3">
                <div className="cost-breakdown__title">Cost Breakdown</div>
                <div className="cost-breakdown__row">
                  <span>{selectedDates.length} day{selectedDates.length > 1 ? "s" : ""} × ₹{entryFee}</span>
                  <span className="cost-breakdown__total">₹{totalCost}</span>
                </div>
                <div className="cost-breakdown__dates">
                  {selectedDates.map(d => (
                    <span key={d} className="cost-breakdown__date-tag">{formatDMY(d)}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Service */}
        <div className="col-12 p-3">
          <FxSelect name="serviceId" variant="outlined" label="Service"
            options={services.length > 0 ? services : [{ id: -1, text: "No services available" }]}
            selectedValue={item.serviceId}
            valueField="id" displayField="text" size="small"
            onValueChange={handleInputChange}
            disabled={item.communityId === -1 || serviceSelectDisabled} />
          {hasError("serviceId", "select") && <div className="error">Service is required</div>}
          {serviceInactiveMsg && (
            <div className="inactive-service-msg mt-2">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {serviceInactiveMsg}
            </div>
          )}
        </div>

        {/* Dynamic questions */}
        {questions.map((que, i) => (
          <div key={i}>
            {que.questionTypeId === 1 && (
              <div className="col-12 p-3">
                <FxInput name={que.identifier} variant="outlined" fullWidth size="small"
                  label={que.description} value={answerMap[que.identifier ?? ""]}
                  onValueChange={handleAnswerInputChange} />
              </div>
            )}
            {que.questionTypeId === 3 && (
              <div className="col-12 p-3">
                <FxSelect name={que.identifier} label={que.description}
                  options={queOptions.filter(qo => qo.questionId === que.id)}
                  selectedValue={answerMap[que.identifier ?? ""]}
                  variant="outlined" valueField="code" displayField="optionText"
                  size="small" onValueChange={handleAnswerInputChange} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 drawer-footer">
        <Button color="primary" variant="contained" fullWidth className="btn-style"
          onClick={submitServiceRequest}
          disabled={!isFormReady}
          style={{ opacity: isFormReady ? 1 : 0.45 }}>
          <b>Submit</b>
        </Button>
      </div>
    </div>
  );
};

const ViewRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="view-row">
    <span className="view-row__label">{label}</span>
    <span className="view-row__value">{value}</span>
  </div>
);

export default SpecialEventEnrol;